import React, { RefCallback, useCallback, useEffect, useMemo, useRef } from "react";
import { TWebsiteBuilderTabsetActiveTabMap, useWebsiteBuilderEditorContext } from "../../websiteBuilderEditorContext";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../utility/websiteBuilderZLayers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { isWebsiteBuilderStretchElementData } from "../../nodes/element/websiteBuilderElement";
import { useDragLayer, XYCoord } from "react-dnd";
import { TWebsiteBuilderActivePageNodeMap } from "redux/reducers/websiteBuilder/websiteBuilderEditor";
import "./websiteBuilderOverlay.scss";
import { WEBSITE_BUILDER_DRAG_LAYER } from "../../WebsiteBuilderEditor";
import Portal from "elements/Portal";
import { isWebsiteBuilderSection } from "../../nodes/section/websiteBuilderSection";
import { TWebsiteBuilderSelectedNode, TWebsiteBuilderSelectedNodeEditingMode } from "../../nodes/websiteBuilderSelectedNode";
import { clamp } from "lodash";
import { isWebsiteBuilderStretchContainerData, isWebsiteBuilderContainerWithChildren } from "../../nodes/container/websiteBuilderContainer";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { isWebsiteBuilderSectionEntity, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH, websiteBuilderGetSectionEntityActiveLayoutData } from "../../nodes/websiteBuilderSectionEntity";
import { TWebsiteBuilderDevice } from "../../website/shared/device";
import { isSectionEntityViewResizeAspectRatioElementType } from "../views/hooks/sectionEntity/useSectionEntityViewResize/useSectionEntityViewResize";
import { isWebsiteBuilderGrid } from "../../nodes/container/websiteBuilderGrid";
import { isWebsiteBuilderGridCell, IWebsiteBuilderGridCell, websiteBuilderGridCellFilled } from "../../nodes/gridCell/websiteBuilderGridCell";
import { isWebsiteBuilderElementContainerParentNode, isWebsiteBuilderElementFlowParentNode, isWebsiteBuilderElementFreeformParentNode, TWebsiteBuilderElementContainerParentNode } from "../../nodes/websiteBuilderNode";
import { isWebsiteBuilderTabset } from "../../nodes/container/websiteBuilderTabset";
import { IWebsiteBuilderTab } from "../../nodes/tab/websiteBuilderTab";

interface IWebsiteBuilderOverlayDragLayerCollectedProps {
  isDragging: boolean;
  clientOffset: XYCoord;
  item: unknown;
}

interface IWebsiteBuilderOverlayDrawRefState {
  deviceView: TWebsiteBuilderDevice;
  selectedNode: TWebsiteBuilderSelectedNode;
  activePageNodeMap: TWebsiteBuilderActivePageNodeMap;
  tabsetActiveTabMap: TWebsiteBuilderTabsetActiveTabMap;
  isDragging: boolean;
  item: unknown;
  clientX: number;
  clientY: number;
}

interface IWebsiteBuilderOverlayDrawTargetRect {
  centerX: number;
  centerY: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

type TWebsiteBuilderOverlayDrawAction = "stop" | "loop" | "continue";

interface IWebsiteBuilderOverlayDrawStopContext {
  type: Extract<TWebsiteBuilderOverlayDrawAction, "stop">;
}

interface IWebsiteBuilderOverlayDrawLoopContext {
  type: Extract<TWebsiteBuilderOverlayDrawAction, "loop">;
}

interface IWebsiteBuilderOverlayDrawContinueContext {
  type: Extract<TWebsiteBuilderOverlayDrawAction, "continue">;
  draggingNodeInCanvas: boolean;
  selectedContainerNodeElementIds: string[];
  selectedNodeIsFlowPositioned: boolean;
  selectedNodeElementSiblingIdsInContainer: string[];
  selectedNodeUsesAspectRatioResize: boolean;
  drawTargetRect: IWebsiteBuilderOverlayDrawTargetRect;
}

type TWebsiteBuilderOverlayDrawContext = 
  | IWebsiteBuilderOverlayDrawStopContext
  | IWebsiteBuilderOverlayDrawLoopContext
  | IWebsiteBuilderOverlayDrawContinueContext;

interface IWebsiteBuilderOverlayLineCore {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

type TWebsiteBuilderOverlayHorizontalLineAnchor = "top" | "center" | "bottom";

interface IWebsiteBuilderOverlayHorizontalLine extends IWebsiteBuilderOverlayLineCore {
  anchor: TWebsiteBuilderOverlayHorizontalLineAnchor;
}

type TWebsiteBuilderOverlayVerticalLineAnchor = "left" | "center" | "right";

interface IWebsiteBuilderOverlayVerticalLine extends IWebsiteBuilderOverlayLineCore {
  anchor: TWebsiteBuilderOverlayVerticalLineAnchor;
}

interface IWebsiteBuilderOverlayLines {
  horizontal: IWebsiteBuilderOverlayHorizontalLine;
  vertical: IWebsiteBuilderOverlayVerticalLine;
}

export type TWebsiteBuilderOverlayLinesRef = React.MutableRefObject<IWebsiteBuilderOverlayLines>;

interface IWebsiteBuilderOverlayResizeDirections {
  strictlyVertical: boolean;
  strictlyHorizontal: boolean;
  diagonal: boolean;
  west: boolean;
  east: boolean;
  north: boolean;
  south: boolean;
}

export function WebsiteBuilderOverlay() {
  const { activePageDomNodeRefMap, tabsetActiveTabMap, designSpaceRef, overlayLinesRef, scrollContainerRef } = useWebsiteBuilderEditorContext();

  const dispatch = useAppDispatch();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);
  const activePageNodeMap = useAppSelector(store => store.websiteBuilderStore.editor.activePageNodeMap);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D>(null);
  const animationFrameHandleRef = useRef<number>(null);
  const dragLayerRef = useRef<HTMLDivElement>(null);

  const drawStateRef = useRef<IWebsiteBuilderOverlayDrawRefState>({
    deviceView: null,
    selectedNode: null,
    activePageNodeMap: null,
    tabsetActiveTabMap: null,
    isDragging: null,
    item: null,
    clientX: null,
    clientY: null,
  });

  const { isDragging, clientOffset, item } = useDragLayer<IWebsiteBuilderOverlayDragLayerCollectedProps>(monitor => {
    return {
      isDragging: monitor.isDragging(),
      clientOffset: monitor.getClientOffset(),
      item: monitor.getItem(),
    };
  });

  const editingModeType = selectedNode?.editingMode?.type;
  
  const active =
    isDragging ||
    editingModeType === "element-move" ||
    editingModeType === "element-resize" ||
    editingModeType === "container-move" ||
    editingModeType === "container-resize";

  const observeResize = !active && editingModeType !== "section-resize";

  const configureCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const designSpace = designSpaceRef.current;

    if (canvas == null || ctx == null || designSpace == null) {
      return;
    }

    const dpr = window.devicePixelRatio ?? 1;

    const designSpaceRect = designSpace.getBoundingClientRect();

    canvas.width = designSpaceRect.width * dpr;
    canvas.height = designSpaceRect.height * dpr;

    canvas.style.width = `${designSpaceRect.width}px`;
    canvas.style.height = `${designSpaceRect.height}px`;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    ctx.lineWidth = 1;
    ctx.strokeStyle = "#986DFF";
    ctx.lineJoin = "miter";
    ctx.lineCap = "square";

    ctx.imageSmoothingEnabled = false;
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1.0;
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout = null;
    let observer: ResizeObserver = null;

    if (observeResize && designSpaceRef.current != null) {
      configureCanvas();

      const debouncedConfigureCanvas = () => {
        clearTimeout(timeout);
        timeout = setTimeout(configureCanvas, 100);
      };

      observer = new ResizeObserver(debouncedConfigureCanvas);
      observer.observe(designSpaceRef.current);
    }

    return () => {
      clearTimeout(timeout);
      observer?.disconnect();
    };
  }, [observeResize, configureCanvas]);

  function setDragLayer(
    dragLayer: HTMLDivElement,
    left: number,
    top: number,
    width: number,
    height: number,
  ) {
    dragLayer.style.display = "block";
    dragLayer.style.left = `${left}px`;
    dragLayer.style.top = `${top}px`;
    dragLayer.style.width = `${width}px`;
    dragLayer.style.height = `${height}px`;
  }

  function clearDragLayer(dragLayer: HTMLDivElement) {
    dragLayer.style.display = null;
    dragLayer.style.left = null;
    dragLayer.style.top = null;
    dragLayer.style.width = null;
    dragLayer.style.height = null;
  }

  function drawLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  function getResizeDirections(editingMode: TWebsiteBuilderSelectedNodeEditingMode): IWebsiteBuilderOverlayResizeDirections {
    if (
      editingMode?.type === "element-resize" ||
      editingMode?.type === "container-resize"
    ) {
      const handle = editingMode.handle;

      const strictlyVertical = handle === "North" || handle === "South";
      const strictlyHorizontal = handle === "West" || handle === "East";
      const diagonal = !strictlyVertical && !strictlyHorizontal;
      const west = handle === "NorthWest" || handle === "West" || handle === "SouthWest";
      const east = handle === "NorthEast" || handle === "East" || handle === "SouthEast";
      const north = handle === "NorthWest" || handle === "North" || handle === "NorthEast";
      const south = handle === "SouthWest" || handle === "South" || handle === "SouthEast";
      
      return {
        strictlyVertical,
        strictlyHorizontal,
        diagonal,
        west,
        east,
        north,
        south,
      };
    } else {
      return {
        strictlyVertical: false,
        strictlyHorizontal: false,
        diagonal: false,
        west: false,
        east: false,
        north: false,
        south: false,
      };
    }
  }

  function pointInsideRect(x: number, y: number, rect: DOMRect): boolean {
    return (
      x >= rect.x &&
      x <= rect.x + rect.width &&
      y >= rect.y &&
      y <= rect.y + rect.height
    );
  }

  function handleStretchedSelectedNodeInDraw(
    selectedNode: TWebsiteBuilderSelectedNode,
    activePageNodeMap: TWebsiteBuilderActivePageNodeMap,
  ): TWebsiteBuilderOverlayDrawAction {
    if (
      selectedNode?.type === "element" ||
      selectedNode?.type === "container"
    ) {
      const selectedDomNode = activePageDomNodeRefMap.current.get(selectedNode.id);
      const selectedPageNode = activePageNodeMap.get(selectedNode.id);

      if (selectedDomNode == null || selectedPageNode == null) {
        return "stop";
      }

      const selectedPageNodeStretched = 
        (selectedPageNode.type === "element" && isWebsiteBuilderStretchElementData(selectedPageNode.element) && selectedPageNode.element.stretch.enabled) ||
        (selectedPageNode.type === "container" && isWebsiteBuilderStretchContainerData(selectedPageNode.container) && selectedPageNode.container.stretch.enabled);

      const selectedDomNodeBoundingRect = selectedDomNode.getBoundingClientRect();

      const selectedDomNodeCenterX = selectedDomNodeBoundingRect.x + (selectedDomNodeBoundingRect.width / 2);
      const selectedDomNodeCenterY = selectedDomNodeBoundingRect.y + (selectedDomNodeBoundingRect.height / 2);

      if (selectedPageNodeStretched) {
        if (
          selectedNode.editingMode?.type === "element-move" ||
          selectedNode.editingMode?.type === "container-move"
        ) {
          let sectionMoveTargetId: string = null;

          for (const [key, domNode] of activePageDomNodeRefMap.current) {
            const node = activePageNodeMap.get(key);
            
            if (
              node == null ||
              domNode == null ||
              node.type !== "section"
            ) {
              continue;
            }

            const boundingRect = domNode.getBoundingClientRect();

            if (pointInsideRect(selectedDomNodeCenterX, selectedDomNodeCenterY, boundingRect)) {
              sectionMoveTargetId = node.id;
              break;
            }
          }

          if (selectedNode.editingMode.sectionMoveTargetId !== sectionMoveTargetId) {
            if (selectedNode.type === "element") {
              dispatch(
                websiteBuilderUpdateSelectedNode({
                  selectedNodeUpdates: {
                    type: selectedNode.type,
                    editingMode: {
                      ...selectedNode.editingMode,
                      sectionMoveTargetId,
                    },
                  },
                }),
              );
            } else {
              dispatch(
                websiteBuilderUpdateSelectedNode({
                  selectedNodeUpdates: {
                    type: selectedNode.type,
                    editingMode: {
                      ...selectedNode.editingMode,
                      sectionMoveTargetId,
                    },
                  },
                }),
              );
            }
          }

          return "loop";
        } else {
          return "stop";
        }
      } else {
        return "continue";
      }
    } else {
      return "continue";
    }
  }

  function getDrawContext(
    canvasBoundingRect: DOMRect,
    resizeDirections: IWebsiteBuilderOverlayResizeDirections,
  ): TWebsiteBuilderOverlayDrawContext {
    const ctx = ctxRef.current;
    const designSpace = designSpaceRef.current;
    const overlayLines = overlayLinesRef.current;
    const dragLayer = dragLayerRef.current;

    let draggingNodeInCanvas = false;
    let selectedContainerNodeElementIds: string[] = [];
    let selectedNodeIsFlowPositioned = false;
    let selectedNodeElementSiblingIdsInContainer: string[] = [];
    let selectedNodeUsesAspectRatioResize = false;
    let drawTargetRect: IWebsiteBuilderOverlayDrawTargetRect = null;

    if (drawStateRef.current.isDragging && isWebsiteBuilderSectionEntity(drawStateRef.current.item)) {
      const { width, height } = websiteBuilderGetSectionEntityActiveLayoutData(drawStateRef.current.item, drawStateRef.current.deviceView).dimensions;

      const left = drawStateRef.current.clientX - width / 2;
      const right = drawStateRef.current.clientX + width / 2;
      const top = drawStateRef.current.clientY - height / 2;
      const bottom = drawStateRef.current.clientY + height / 2;

      if (
        drawStateRef.current.clientX < canvasBoundingRect.left ||
        drawStateRef.current.clientX > canvasBoundingRect.right ||
        drawStateRef.current.clientY < canvasBoundingRect.top ||
        drawStateRef.current.clientY > canvasBoundingRect.bottom
      ) {
        setDragLayer(dragLayer, left, top, width, height);

        ctx.clearRect(0, 0, canvasBoundingRect.width, canvasBoundingRect.height);

        overlayLines.vertical = null;
        overlayLines.horizontal = null;

        return {
          type: "loop",
        };
      }

      draggingNodeInCanvas = true;

      drawTargetRect = {
        centerX: drawStateRef.current.clientX,
        centerY: drawStateRef.current.clientY,
        left,
        right,
        top,
        bottom,
        width,
        height,
      };

      return {
        type: "continue",
        draggingNodeInCanvas,
        selectedContainerNodeElementIds,
        selectedNodeIsFlowPositioned,
        selectedNodeElementSiblingIdsInContainer,
        selectedNodeUsesAspectRatioResize, 
        drawTargetRect,
      };
    } else if (
      drawStateRef.current.selectedNode?.type === "element" ||
      drawStateRef.current.selectedNode?.type === "container"
    ) {
      const selectedDomNode = activePageDomNodeRefMap.current.get(drawStateRef.current.selectedNode.id);
      const selectedPageNode = drawStateRef.current.activePageNodeMap.get(drawStateRef.current.selectedNode.id);

      if (selectedDomNode == null || selectedPageNode == null) {
        return {
          type: "stop",
        };
      }

      selectedNodeUsesAspectRatioResize =
        drawStateRef.current.selectedNode.type === "element" &&
        selectedPageNode.type === "element" &&
        isSectionEntityViewResizeAspectRatioElementType(selectedPageNode.element.type);

      if (isWebsiteBuilderContainerWithChildren(selectedPageNode)) {
        if (isWebsiteBuilderElementContainerParentNode(selectedPageNode)) {
          selectedContainerNodeElementIds = selectedPageNode.children
            .filter(child => child.element.visibility[drawStateRef.current.deviceView])
            .map(child => child.id);
        } else if (isWebsiteBuilderGrid(selectedPageNode)) {
          selectedContainerNodeElementIds = selectedPageNode.children
            .filter(websiteBuilderGridCellFilled)
            .map(child => child.children[0])
            .filter(child => child.element.visibility[drawStateRef.current.deviceView])
            .map(child => child.id);
        } else if (isWebsiteBuilderTabset(selectedPageNode)) {
          const activeTabId = drawStateRef.current.tabsetActiveTabMap.get(selectedPageNode.id);

          const activeTab = selectedPageNode.children.find(tab => (
            tab.id === activeTabId
          ));

          selectedContainerNodeElementIds =
            activeTab?.children
              .filter(child => child.element.visibility[drawStateRef.current.deviceView])
              .map(child => child.id) ?? [];
        }
      }

      const boundingRect = selectedDomNode.getBoundingClientRect();
      const designSpaceRect = designSpace.getBoundingClientRect();
      
      let width: number = null;
      let height: number = null;
      let left: number = null;
      let top: number = null;
      
      if (
        drawStateRef.current.selectedNode.editingMode?.type === "element-move" ||
        drawStateRef.current.selectedNode.editingMode?.type === "container-move"
      ) {
        const { designSpaceLeftXBoundary, designSpaceRightXBoundary } = drawStateRef.current.selectedNode.editingMode;
        const { x: mouseOffsetX, y: mouseOffsetY } = drawStateRef.current.selectedNode.editingMode.mouseOffset;

        const leftBound = designSpaceRect.left + designSpaceLeftXBoundary;
        const rightBound = designSpaceRect.left + designSpaceRightXBoundary - boundingRect.width;
        const topBound = designSpaceRect.top;
        const bottomBound = designSpaceRect.bottom - boundingRect.height;

        width = boundingRect.width;
        height = boundingRect.height;
        left = clamp(drawStateRef.current.clientX - mouseOffsetX, leftBound, rightBound);
        top = clamp(drawStateRef.current.clientY - mouseOffsetY, topBound, bottomBound);
      } else if (
        drawStateRef.current.selectedNode.editingMode?.type === "element-resize" ||
        drawStateRef.current.selectedNode.editingMode?.type === "container-resize"
      ) {
        width = boundingRect.width;
        height = boundingRect.height;
        left = boundingRect.left;
        top = boundingRect.top;

        let westBound = Infinity;
        let eastBound = Infinity;
        let northBound = Infinity;
        let southBound = Infinity;

        if (
          drawStateRef.current.selectedNode.editingMode.context.type === "element-in-section" ||
          drawStateRef.current.selectedNode.editingMode.context.type === "container-in-section"
        ) {
          const { sectionTop, sectionBottom, designSpaceLeftXBoundary, designSpaceRightXBoundary } = drawStateRef.current.selectedNode.editingMode.context;

          westBound = designSpaceRect.left + designSpaceLeftXBoundary;
          eastBound = designSpaceRect.left + designSpaceRightXBoundary - boundingRect.left;
          northBound = sectionTop;
          southBound = sectionBottom - boundingRect.top;
        } else if (
          drawStateRef.current.selectedNode.type === "element" &&
          drawStateRef.current.selectedNode.editingMode.context.type === "element-in-container"
        ) {
          const { containerTop, containerRight, containerBottom, containerLeft } = drawStateRef.current.selectedNode.editingMode.context;

          westBound = containerLeft;
          eastBound = containerRight - boundingRect.left;
          northBound = containerTop;
          southBound = containerBottom - boundingRect.top;

          const parentNode = drawStateRef.current.activePageNodeMap.get(drawStateRef.current.selectedNode.containerId);
          
          if (isWebsiteBuilderElementContainerParentNode(parentNode)) {
            selectedNodeElementSiblingIdsInContainer = parentNode.children
              .filter(child => child.id !== drawStateRef.current.selectedNode.id && child.element.visibility[drawStateRef.current.deviceView])
              .map(child => child.id);

            selectedNodeIsFlowPositioned = isWebsiteBuilderElementFlowParentNode(parentNode);
          } else {
            return {
              type: "stop",
            };
          }
        } else if (
          drawStateRef.current.selectedNode.type === "element" &&
          drawStateRef.current.selectedNode.editingMode.context.type === "element-in-grid-cell"
        ) {
          const { gridTop, gridRight, gridBottom, gridLeft } = drawStateRef.current.selectedNode.editingMode.context;

          westBound = gridLeft;
          eastBound = gridRight - boundingRect.left;
          northBound = gridTop;
          southBound = gridBottom - boundingRect.top;

          const parentNode = drawStateRef.current.activePageNodeMap.get(drawStateRef.current.selectedNode.gridCellId);
          const containerNode = drawStateRef.current.activePageNodeMap.get(drawStateRef.current.selectedNode.containerId);
          
          if (isWebsiteBuilderGridCell(parentNode) && isWebsiteBuilderGrid(containerNode)) {
            selectedNodeElementSiblingIdsInContainer = containerNode.children
              .filter(websiteBuilderGridCellFilled)
              .map(child => child.children[0])
              .filter(child => child.id !== drawStateRef.current.selectedNode.id && child.element.visibility[drawStateRef.current.deviceView])
              .map(child => child.id);

            selectedNodeIsFlowPositioned = isWebsiteBuilderElementFlowParentNode(parentNode);
          } else {
            return {
              type: "stop",
            };
          }
        } else if (
          drawStateRef.current.selectedNode.type === "element" &&
          drawStateRef.current.selectedNode.editingMode.context.type === "element-in-tab"
        ) {
          const { tabTop, tabRight, tabBottom, tabLeft } = drawStateRef.current.selectedNode.editingMode.context;

          westBound = tabLeft;
          eastBound = tabRight - boundingRect.left;
          northBound = tabTop;
          southBound = tabBottom - boundingRect.top;

          const parentNode = drawStateRef.current.activePageNodeMap.get(drawStateRef.current.selectedNode.tabId);
          const containerNode = drawStateRef.current.activePageNodeMap.get(drawStateRef.current.selectedNode.containerId);

          if (parentNode?.type === "tab" && isWebsiteBuilderTabset(containerNode)) {
            selectedNodeElementSiblingIdsInContainer = parentNode.children
              .filter(child => child.id !== drawStateRef.current.selectedNode.id && child.element.visibility[drawStateRef.current.deviceView])
              .map(child => child.id);

            selectedNodeIsFlowPositioned = isWebsiteBuilderElementFlowParentNode(parentNode);
          } else {
            return {
              type: "stop",
            };
          }
        }

        if (resizeDirections.west) {
          left = Math.max(Math.min(drawStateRef.current.clientX, boundingRect.right - WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH), westBound);
          width = Math.max(boundingRect.right - left, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH);
        }

        if (resizeDirections.east) {
          width = Math.min(Math.max(drawStateRef.current.clientX - boundingRect.left, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH), eastBound);
        }

        if (resizeDirections.north) {
          top = Math.max(Math.min(drawStateRef.current.clientY, boundingRect.bottom - WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT), northBound);
          height = Math.max(boundingRect.bottom - top, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT);
        }

        if (resizeDirections.south) {
          height = Math.min(Math.max(drawStateRef.current.clientY - boundingRect.top, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT), southBound);
        }
      }

      if (
        width == null ||
        height == null ||
        left == null ||
        top == null
      ) {
        return {
          type: "stop",
        };
      }

      const right = left + width;
      const bottom = top + height;
      const centerX = left + width / 2;
      const centerY = top + height / 2;

      drawTargetRect = {
        centerX,
        centerY,
        left,
        right,
        top,
        bottom,
        width,
        height,
      };

      return {
        type: "continue",
        draggingNodeInCanvas,
        selectedContainerNodeElementIds,
        selectedNodeIsFlowPositioned,
        selectedNodeElementSiblingIdsInContainer,
        selectedNodeUsesAspectRatioResize, 
        drawTargetRect,
      };
    } else {
      return {
        type: "stop",
      };
    }
  }

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const designSpace = designSpaceRef.current;
    const overlayLines = overlayLinesRef.current;
    const dragLayer = dragLayerRef.current;

    if (canvas == null || ctx == null || designSpace == null || overlayLines == null || dragLayer == null) {
      return;
    }

    if (drawStateRef.current.isDragging && isWebsiteBuilderSection(drawStateRef.current.item)) {
      const width = 400;
      const height = 200;

      const left = drawStateRef.current.clientX - width / 2;
      const top = drawStateRef.current.clientY - height / 2;

      setDragLayer(dragLayer, left, top, width, height);

      animationFrameHandleRef.current = requestAnimationFrame(draw);
      return;
    }

    const stretchedSelectedNodeAction = handleStretchedSelectedNodeInDraw(drawStateRef.current.selectedNode, drawStateRef.current.activePageNodeMap);

    if (stretchedSelectedNodeAction === "loop") {
      animationFrameHandleRef.current = requestAnimationFrame(draw);
      return;
    } else if (stretchedSelectedNodeAction === "stop") {
      return;
    }

    const canvasBoundingRect = canvas.getBoundingClientRect();
    const resizeDirections = getResizeDirections(drawStateRef.current.selectedNode?.editingMode);
    
    const context: TWebsiteBuilderOverlayDrawContext = getDrawContext(canvasBoundingRect, resizeDirections);

    if (context.type === "loop") {
      animationFrameHandleRef.current = requestAnimationFrame(draw);
      return;
    } else if (context.type === "stop") {
      return;
    }

    const movingElement =
      drawStateRef.current.selectedNode?.editingMode?.type === "element-move";
    
    const movingSectionEntity =
      movingElement ||
      drawStateRef.current.selectedNode?.editingMode?.type === "container-move";
    
    const scrollContainerRect = scrollContainerRef.current.getBoundingClientRect();

    const minDeltaBound = 5;
    
    let horizontalLine: IWebsiteBuilderOverlayHorizontalLine = null;
    let verticalLine: IWebsiteBuilderOverlayVerticalLine = null;
    
    let lowestXDelta = Infinity;
    let lowestYDelta = Infinity;

    let sectionMoveTargetId: string = null;

    let containerMoveTarget: TWebsiteBuilderElementContainerParentNode = null;

    let gridCellMoveTarget: IWebsiteBuilderGridCell = null;
    let gridCellMoveTargetParentZIndex: number = null;

    let tabMoveTarget: IWebsiteBuilderTab = null;
    let tabMoveTargetParentZIndex: number = null;

    for (const [key, domNode] of activePageDomNodeRefMap.current) {
      const node = drawStateRef.current.activePageNodeMap.get(key);

      if (
        node == null ||
        domNode == null ||
        drawStateRef.current.selectedNode?.id === key ||
        context.selectedContainerNodeElementIds.includes(key)
      ) {
        continue;
      }

      if (node.type === "root" && !resizeDirections.strictlyVertical) {
        const boundingRect = domNode.getBoundingClientRect();
        const boundingCenterX = boundingRect.left + boundingRect.width / 2;

        const xDelta = Math.abs(context.drawTargetRect.centerX - boundingCenterX);

        if (xDelta <= minDeltaBound && xDelta < lowestXDelta) {
          lowestXDelta = xDelta;

          verticalLine = {
            anchor: "center",
            x1: boundingCenterX,
            y1: 0,
            x2: boundingCenterX,
            y2: canvasBoundingRect.bottom,
          };
        }
      } else if (node.type === "section") {
        const boundingRect = domNode.getBoundingClientRect();

        if (
          movingSectionEntity &&
          pointInsideRect(context.drawTargetRect.centerX, context.drawTargetRect.centerY, boundingRect)
        ) {
          sectionMoveTargetId = node.id;
        }

        if (!resizeDirections.strictlyHorizontal) {
          const boundingCenterY = boundingRect.top + boundingRect.height / 2;

          const yDelta = Math.abs(context.drawTargetRect.centerY - boundingCenterY);

          if (yDelta <= minDeltaBound && yDelta < lowestYDelta) {
            lowestYDelta = yDelta;

            horizontalLine = {
              anchor: "center",
              x1: 0,
              y1: boundingCenterY,
              x2: canvasBoundingRect.right,
              y2: boundingCenterY,
            };
          }
        }
      } else if (isWebsiteBuilderSectionEntity(node)) {
        const boundingRect = domNode.getBoundingClientRect();
        const boundingCenterX = boundingRect.left + boundingRect.width / 2;
        const boundingCenterY = boundingRect.top + boundingRect.height / 2;

        const outOfBounds =
          boundingRect.bottom < scrollContainerRect.top ||
          boundingRect.top > scrollContainerRect.bottom;

        if (outOfBounds) {
          continue;
        }

        if (
          movingElement &&
          isWebsiteBuilderElementContainerParentNode(node) &&
          (node.container.zIndex > (containerMoveTarget?.container.zIndex ?? -1)) &&
          pointInsideRect(context.drawTargetRect.centerX, context.drawTargetRect.centerY, boundingRect)
        ) {
          containerMoveTarget = node;
          
          continue;
        }

        if (
          movingElement &&
          isWebsiteBuilderGrid(node) &&
          (node.container.zIndex > (gridCellMoveTargetParentZIndex ?? -1)) &&
          pointInsideRect(context.drawTargetRect.centerX, context.drawTargetRect.centerY, boundingRect)
        ) {
          for (const gridCellNode of node.children) {
            const gridCellNodeRef = activePageDomNodeRefMap.current.get(gridCellNode.id);
            const gridCellNodeBoundingRect = gridCellNodeRef?.getBoundingClientRect();

            if (gridCellNodeBoundingRect == null) {
              continue;
            }

            if (pointInsideRect(context.drawTargetRect.centerX, context.drawTargetRect.centerY, gridCellNodeBoundingRect)) {
              gridCellMoveTarget = gridCellNode;
              gridCellMoveTargetParentZIndex = node.container.zIndex;

              break;
            }
          }

          continue;
        }

        if (
          movingElement &&
          isWebsiteBuilderTabset(node) &&
          (node.container.zIndex > (tabMoveTargetParentZIndex ?? -1)) &&
          pointInsideRect(context.drawTargetRect.centerX, context.drawTargetRect.centerY, boundingRect)
        ) {
          const activeTabId = drawStateRef.current.tabsetActiveTabMap.get(node.id);

          const activeTab = node.children.find(tab => (
            tab.id === activeTabId
          ));

          if (activeTab == null) {
            continue;
          }

          const activeTabNodeRef = activePageDomNodeRefMap.current.get(activeTab.id);
          const activeTabNodeBoundingRect = activeTabNodeRef?.getBoundingClientRect();

          if (activeTabNodeBoundingRect == null) {
            continue;
          }

          if (pointInsideRect(context.drawTargetRect.centerX, context.drawTargetRect.centerY, activeTabNodeBoundingRect)) {
            tabMoveTarget = activeTab;
            tabMoveTargetParentZIndex = node.container.zIndex;
          }

          continue;
        }

        const stretchedNode =
          (node.type === "element" && isWebsiteBuilderStretchElementData(node.element) && node.element.stretch.enabled) ||
          (node.type === "container" && isWebsiteBuilderStretchContainerData(node.container) && node.container.stretch.enabled);

        if (stretchedNode) {
          continue;
        }

        const isElementSiblingInContainer = context.selectedNodeElementSiblingIdsInContainer.includes(node.id);
        const disableCenterLines = isElementSiblingInContainer && context.selectedNodeIsFlowPositioned;

        if (context.drawTargetRect.left < boundingRect.left && !resizeDirections.strictlyHorizontal) {
          const topToTopDelta = resizeDirections.south ? Infinity : Math.abs(context.drawTargetRect.top - boundingRect.top);
          const topToBottomDelta = resizeDirections.south ? Infinity : Math.abs(context.drawTargetRect.top - boundingRect.bottom);
          const centerToCenterDelta = disableCenterLines ? Infinity : Math.abs(context.drawTargetRect.centerY - boundingCenterY);
          const bottomToTopDelta = resizeDirections.north ? Infinity : Math.abs(context.drawTargetRect.bottom - boundingRect.top);
          const bottomToBottomDelta = resizeDirections.north ? Infinity : Math.abs(context.drawTargetRect.bottom - boundingRect.bottom);

          const minYDelta = Math.min(
            topToTopDelta,
            topToBottomDelta,
            centerToCenterDelta,
            bottomToTopDelta,
            bottomToBottomDelta,
          );

          if (minYDelta <= minDeltaBound && minYDelta < lowestYDelta) {
            lowestYDelta = minYDelta;

            if (topToTopDelta === minYDelta) {
              horizontalLine = {
                anchor: "top",
                x1: boundingRect.left,
                y1: boundingRect.top,
                x2: 0,
                y2: boundingRect.top,
              };
            } else if (topToBottomDelta === minYDelta) {
              horizontalLine = {
                anchor: "top",
                x1: boundingRect.left,
                y1: boundingRect.bottom,
                x2: 0,
                y2: boundingRect.bottom,
              };
            } else if (centerToCenterDelta === minYDelta) {
              horizontalLine = {
                anchor: "center",
                x1: boundingRect.left,
                y1: boundingCenterY,
                x2: 0,
                y2: boundingCenterY,
              };
            } else if (bottomToTopDelta === minYDelta) {
              horizontalLine = {
                anchor: "bottom",
                x1: boundingRect.left,
                y1: boundingRect.top,
                x2: 0,
                y2: boundingRect.top,
              };
            } else if (bottomToBottomDelta === minYDelta) {
              horizontalLine = {
                anchor: "bottom",
                x1: boundingRect.left,
                y1: boundingRect.bottom,
                x2: 0,
                y2: boundingRect.bottom,
              };
            }
          }
        }

        if (context.drawTargetRect.right > boundingRect.right && !resizeDirections.strictlyHorizontal) {
          const topToTopDelta = resizeDirections.south ? Infinity : Math.abs(context.drawTargetRect.top - boundingRect.top);
          const topToBottomDelta = resizeDirections.south ? Infinity : Math.abs(context.drawTargetRect.top - boundingRect.bottom);
          const centerToCenterDelta = disableCenterLines ? Infinity : Math.abs(context.drawTargetRect.centerY - boundingCenterY);
          const bottomToTopDelta = resizeDirections.north ? Infinity : Math.abs(context.drawTargetRect.bottom - boundingRect.top);
          const bottomToBottomDelta = resizeDirections.north ? Infinity : Math.abs(context.drawTargetRect.bottom - boundingRect.bottom);

          const minYDelta = Math.min(
            topToTopDelta,
            topToBottomDelta,
            centerToCenterDelta,
            bottomToTopDelta,
            bottomToBottomDelta,
          );

          if (minYDelta <= minDeltaBound && minYDelta < lowestYDelta) {
            lowestYDelta = minYDelta;

            if (topToTopDelta === minYDelta) {
              horizontalLine = {
                anchor: "top",
                x1: boundingRect.right,
                y1: boundingRect.top,
                x2: canvasBoundingRect.right,
                y2: boundingRect.top,
              };
            } else if (topToBottomDelta === minYDelta) {
              horizontalLine = {
                anchor: "top",
                x1: boundingRect.right,
                y1: boundingRect.bottom,
                x2: canvasBoundingRect.right,
                y2: boundingRect.bottom,
              };
            } else if (centerToCenterDelta === minYDelta) {
              horizontalLine = {
                anchor: "center",
                x1: boundingRect.right,
                y1: boundingCenterY,
                x2: canvasBoundingRect.right,
                y2: boundingCenterY,
              };
            } else if (bottomToTopDelta === minYDelta) {
              horizontalLine = {
                anchor: "bottom",
                x1: boundingRect.right,
                y1: boundingRect.top,
                x2: canvasBoundingRect.right,
                y2: boundingRect.top,
              };
            } else if (bottomToBottomDelta === minYDelta) {
              horizontalLine = {
                anchor: "bottom",
                x1: boundingRect.right,
                y1: boundingRect.bottom,
                x2: canvasBoundingRect.right,
                y2: boundingRect.bottom,
              };
            }
          }
        }

        if (context.drawTargetRect.top < boundingRect.top && !resizeDirections.strictlyVertical) {
          const leftToLeftDelta = resizeDirections.east ? Infinity : Math.abs(context.drawTargetRect.left - boundingRect.left);
          const leftToRightDelta = resizeDirections.east ? Infinity : Math.abs(context.drawTargetRect.left - boundingRect.right);
          const centerToCenterDelta = disableCenterLines ? Infinity : Math.abs(context.drawTargetRect.centerX - boundingCenterX);
          const rightToLeftDelta = resizeDirections.west ? Infinity : Math.abs(context.drawTargetRect.right - boundingRect.left);
          const rightToRightDelta = resizeDirections.west ? Infinity : Math.abs(context.drawTargetRect.right - boundingRect.right);

          const minXDelta = Math.min(
            leftToLeftDelta,
            leftToRightDelta,
            centerToCenterDelta,
            rightToLeftDelta,
            rightToRightDelta,
          );

          if (minXDelta <= minDeltaBound && minXDelta < lowestXDelta) {
            lowestXDelta = minXDelta;

            if (leftToLeftDelta === minXDelta) {
              verticalLine = {
                anchor: "left",
                x1: boundingRect.left,
                y1: boundingRect.top,
                x2: boundingRect.left,
                y2: 0,
              };
            } else if (leftToRightDelta === minXDelta) {
              verticalLine = {
                anchor: "left",
                x1: boundingRect.right,
                y1: boundingRect.top,
                x2: boundingRect.right,
                y2: 0,
              };
            } else if (centerToCenterDelta === minXDelta) {
              verticalLine = {
                anchor: "center",
                x1: boundingCenterX,
                y1: boundingRect.top,
                x2: boundingCenterX,
                y2: 0,
              };
            } else if (rightToLeftDelta === minXDelta) {
              verticalLine = {
                anchor: "right",
                x1: boundingRect.left,
                y1: boundingRect.top,
                x2: boundingRect.left,
                y2: 0,
              };
            } else if (rightToRightDelta === minXDelta) {
              verticalLine = {
                anchor: "right",
                x1: boundingRect.right,
                y1: boundingRect.top,
                x2: boundingRect.right,
                y2: 0,
              };
            }
          }
        }

        if (context.drawTargetRect.bottom > boundingRect.bottom && !resizeDirections.strictlyVertical) {
          const leftToLeftDelta = resizeDirections.east ? Infinity : Math.abs(context.drawTargetRect.left - boundingRect.left);
          const leftToRightDelta = resizeDirections.east ? Infinity : Math.abs(context.drawTargetRect.left - boundingRect.right);
          const centerToCenterDelta = disableCenterLines ? Infinity : Math.abs(context.drawTargetRect.centerX - boundingCenterX);
          const rightToLeftDelta = resizeDirections.west ? Infinity : Math.abs(context.drawTargetRect.right - boundingRect.left);
          const rightToRightDelta = resizeDirections.west ? Infinity : Math.abs(context.drawTargetRect.right - boundingRect.right);

          const minXDelta = Math.min(
            leftToLeftDelta,
            leftToRightDelta,
            centerToCenterDelta,
            rightToLeftDelta,
            rightToRightDelta,
          );

          if (minXDelta <= minDeltaBound && minXDelta < lowestXDelta) {
            lowestXDelta = minXDelta;

            if (leftToLeftDelta === minXDelta) {
              verticalLine = {
                anchor: "left",
                x1: boundingRect.left,
                y1: boundingRect.bottom,
                x2: boundingRect.left,
                y2: canvasBoundingRect.bottom,
              };
            } else if (leftToRightDelta === minXDelta) {
              verticalLine = {
                anchor: "left",
                x1: boundingRect.right,
                y1: boundingRect.bottom,
                x2: boundingRect.right,
                y2: canvasBoundingRect.bottom,
              };
            } else if (centerToCenterDelta === minXDelta) {
              verticalLine = {
                anchor: "center",
                x1: boundingCenterX,
                y1: boundingRect.bottom,
                x2: boundingCenterX,
                y2: canvasBoundingRect.bottom,
              };
            } else if (rightToLeftDelta === minXDelta) {
              verticalLine = {
                anchor: "right",
                x1: boundingRect.left,
                y1: boundingRect.bottom,
                x2: boundingRect.left,
                y2: canvasBoundingRect.bottom,
              };
            } else if (rightToRightDelta === minXDelta) {
              verticalLine = {
                anchor: "right",
                x1: boundingRect.right,
                y1: boundingRect.bottom,
                x2: boundingRect.right,
                y2: canvasBoundingRect.bottom,
              };
            }
          }
        }
      }
    }

    if (
      containerMoveTarget != null &&
      containerMoveTarget.container.zIndex >= (gridCellMoveTargetParentZIndex ?? -1) &&
      containerMoveTarget.container.zIndex >= (tabMoveTargetParentZIndex ?? -1)
    ) {
      gridCellMoveTarget = null;
      tabMoveTarget = null;
    } else if (
      gridCellMoveTarget != null &&
      gridCellMoveTargetParentZIndex >= (containerMoveTarget?.container.zIndex ?? -1) &&
      gridCellMoveTargetParentZIndex >= (tabMoveTargetParentZIndex ?? -1)
    ) {
      containerMoveTarget = null;
      tabMoveTarget = null;
    } else if (
      tabMoveTarget != null &&
      tabMoveTargetParentZIndex >= (gridCellMoveTargetParentZIndex ?? -1) &&
      tabMoveTargetParentZIndex >= (containerMoveTarget?.container.zIndex ?? -1)
    ) {
      gridCellMoveTarget = null;
      containerMoveTarget = null;
    }

    const containerMoveTargetId = containerMoveTarget?.id ?? null;
    const gridCellMoveTargetId = gridCellMoveTarget?.id ?? null;
    const tabMoveTargetId = tabMoveTarget?.id ?? null;

    if (
      drawStateRef.current.selectedNode?.editingMode?.type === "element-move" &&
      (
        drawStateRef.current.selectedNode.editingMode.sectionMoveTargetId !== sectionMoveTargetId ||
        drawStateRef.current.selectedNode.editingMode.containerMoveTargetId !== containerMoveTargetId ||
        drawStateRef.current.selectedNode.editingMode.gridCellMoveTargetId !== gridCellMoveTargetId ||
        drawStateRef.current.selectedNode.editingMode.tabMoveTargetId !== tabMoveTargetId
      )
    ) {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            type: "element",
            editingMode: {
              ...drawStateRef.current.selectedNode.editingMode,
              sectionMoveTargetId,
              containerMoveTargetId,
              gridCellMoveTargetId,
              tabMoveTargetId,
            },
          },
        }),
      );
    }

    if (
      drawStateRef.current.selectedNode?.editingMode?.type === "container-move" &&
      drawStateRef.current.selectedNode.editingMode.sectionMoveTargetId !== sectionMoveTargetId
    ) {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            type: "container",
            editingMode: {
              ...drawStateRef.current.selectedNode.editingMode,
              sectionMoveTargetId,
            },
          },
        }),
      );
    }

    ctx.clearRect(0, 0, canvasBoundingRect.width, canvasBoundingRect.height);

    overlayLines.horizontal = null;
    overlayLines.vertical = null;

    const activeMoveTarget = containerMoveTarget ?? gridCellMoveTarget ?? tabMoveTarget;

    if (
      context.draggingNodeInCanvas ||
      activeMoveTarget == null ||
      isWebsiteBuilderElementFreeformParentNode(activeMoveTarget)
    ) {
      ctx.beginPath();

      if (context.selectedNodeUsesAspectRatioResize && horizontalLine != null && verticalLine != null && resizeDirections.diagonal) {
        if (lowestXDelta < lowestYDelta) {
          horizontalLine = null;
        } else {
          verticalLine = null;
        }
      }

      if (horizontalLine != null) {
        const { x1, y1, x2, y2 } = horizontalLine;

        drawLine(
          ctx,
          x1 - canvasBoundingRect.left,
          y1 - canvasBoundingRect.top,
          x2 - canvasBoundingRect.left,
          y2 - canvasBoundingRect.top,
        );

        overlayLines.horizontal = horizontalLine;
      }

      if (verticalLine != null) {
        const { x1, y1, x2, y2 } = verticalLine;

        drawLine(
          ctx,
          x1 - canvasBoundingRect.left,
          y1 - canvasBoundingRect.top,
          x2 - canvasBoundingRect.left,
          y2 - canvasBoundingRect.top,
        );

        overlayLines.vertical = verticalLine;
      }

      ctx.closePath();

      if (context.draggingNodeInCanvas) {
        let left = context.drawTargetRect.left;
        let top = context.drawTargetRect.top;

        const width = context.drawTargetRect.width;
        const height = context.drawTargetRect.height;

        if (horizontalLine != null) {
          const { anchor, y1 } = horizontalLine;

          switch (anchor) {
            case "top": {
              top = y1;
              break;
            }
            case "center": {
              top = y1 - height / 2;
              break;
            }
            case "bottom": {
              top = y1 - height;
              break;
            }
          }
        } if (verticalLine != null) {
          const { anchor, x1 } = verticalLine;

          switch (anchor) {
            case "left": {
              left = x1;
              break;
            }
            case "center": {
              left = x1 - width / 2;
              break;
            }
            case "right": {
              left = x1 - width;
              break;
            }
          }
        }

        setDragLayer(dragLayer, left, top, width, height);
      }
    }
    
    animationFrameHandleRef.current = requestAnimationFrame(draw);
  }, []);

  const cleanup = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const overlayLines = overlayLinesRef.current;
    const dragLayer = dragLayerRef.current;

    cancelAnimationFrame(animationFrameHandleRef.current);
    animationFrameHandleRef.current = null;

    if (canvas != null && ctx != null) {
      const canvasBoundingRect = canvas.getBoundingClientRect();

      ctx.clearRect(0, 0, canvasBoundingRect.width, canvasBoundingRect.height);
    }

    if (overlayLines != null) {
      overlayLines.vertical = null;
      overlayLines.horizontal = null;
    }

    if (dragLayer != null) {
      clearDragLayer(dragLayer);
    }
  }, []);

  useEffect(() => {
    drawStateRef.current.deviceView = deviceView;
    drawStateRef.current.selectedNode = selectedNode;
    drawStateRef.current.activePageNodeMap = activePageNodeMap;
    drawStateRef.current.tabsetActiveTabMap = tabsetActiveTabMap;
    drawStateRef.current.isDragging = isDragging;
    drawStateRef.current.item = item;
  }, [deviceView, selectedNode, activePageNodeMap, tabsetActiveTabMap, isDragging, item]);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    drawStateRef.current.clientX = e.clientX;
    drawStateRef.current.clientY = e.clientY;
  }, []);

  useEffect(() => {
    const notDragging = !isDragging || clientOffset == null;

    if (notDragging) {
      document.addEventListener("pointermove", handlePointerMove);
    } else {
      drawStateRef.current.clientX = clientOffset.x;
      drawStateRef.current.clientY = clientOffset.y;
    }

    return () => {
      if (notDragging) {
        document.removeEventListener("pointermove", handlePointerMove);
      }
    };
  }, [isDragging, clientOffset, handlePointerMove]);

  useEffect(() => {
    if (active) {
      draw();
    }

    return () => {
      cleanup();
    };
  }, [active, draw, cleanup]);

  const handleCanvasMount = useCallback<RefCallback<HTMLCanvasElement>>(canvas => {
    if (canvas != null) {
      canvasRef.current = canvas;
      ctxRef.current = canvas.getContext("2d");
    } else {
      canvasRef.current = null;
      ctxRef.current = null;
    }
  }, []);

  const canvas: JSX.Element = useMemo(() => {
    return (
      <canvas
        ref={handleCanvasMount}
        className="website-builder-overlay"
        style={{
          zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.overlay,
        }}
      />
    );
  }, []);

  const dragLayer: JSX.Element = useMemo(() => {
    return (
      <div
        ref={dragLayerRef}
        className="website-builder-overlay-drag-layer"
        style={{
          zIndex: WEBSITE_BUILDER_Z_LAYERS.dragLayer,
        }}
      />
    );
  }, []);

  return (
    <>
      {canvas}
      <Portal isMounted={true} parentId={WEBSITE_BUILDER_DRAG_LAYER}>
        {dragLayer}
      </Portal>
    </>
  );
}